import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Store, ClipboardCheck, PackageCheck, Truck, AlertTriangle, DollarSign, CheckCircle2, ShoppingBag, Timer } from 'lucide-react';
import { api } from '@/lib/api';

const Stat = ({ icon: Icon, label, value, to, accent }) => (
  <Link to={to} className="bg-white rounded-2xl p-6 border border-forest/10 shadow-card hover:shadow-hover transition-shadow" data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
    <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 ${accent || 'bg-forest/10'}`}><Icon className="w-5 h-5 text-forest" /></div>
    <p className="font-playfair text-3xl font-bold text-dark">{value}</p>
    <p className="font-manrope text-sm text-dark/60 mt-1">{label}</p>
  </Link>
);

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [pipeline, setPipeline] = useState({});
  const [live, setLive] = useState(null);

  useEffect(() => {
    api.get('/admin/dashboard').then((r) => setD(r.data)).catch(() => {});
    api.get('/admin/pipeline').then((r) => setPipeline(r.data)).catch(() => {});
    const loadLive = () => api.get('/admin/dashboard/live').then((r) => setLive(r.data)).catch(() => {});
    loadLive();
    const interval = setInterval(loadLive, 20000);
    return () => clearInterval(interval);
  }, []);

  if (!d) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-forest" /></div>;

  return (
    <div data-testid="admin-dashboard">
      <p className="font-manrope text-dark/60 mb-6">Full oversight of farm partners, products, orders, and payments.</p>

      {live && (
        <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10 mb-8" data-testid="live-order-summary">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <h2 className="font-playfair text-2xl font-bold text-dark">Live Order Summary</h2>
            <span className="flex items-center gap-2 text-xs font-manrope text-dark/50"><Timer className="w-4 h-4" />Updates every 20s &middot; source of truth for order flow</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-cream rounded-xl p-4" data-testid="live-order-count"><ShoppingBag className="w-5 h-5 text-forest mb-2" /><p className="font-playfair text-2xl font-bold text-dark">{live.order_count}</p><p className="text-xs font-manrope text-dark/60">Total paid orders</p></div>
            <div className="bg-cream rounded-xl p-4" data-testid="live-revenue"><DollarSign className="w-5 h-5 text-forest mb-2" /><p className="font-playfair text-2xl font-bold text-dark">${live.total_revenue.toFixed(2)}</p><p className="text-xs font-manrope text-dark/60">Total revenue</p></div>
            <div className="bg-cream rounded-xl p-4" data-testid="live-cutoff"><Timer className="w-5 h-5 text-forest mb-2" /><p className="font-playfair text-2xl font-bold text-dark">{live.days_until_cutoff === 0 ? 'Today' : `${live.days_until_cutoff}d`}</p><p className="text-xs font-manrope text-dark/60">Until {live.cutoff_day} {live.cutoff_time} cutoff</p></div>
            <div className="bg-cream rounded-xl p-4" data-testid="live-orders-since-cutoff"><PackageCheck className="w-5 h-5 text-forest mb-2" /><p className="font-playfair text-2xl font-bold text-dark">{live.orders_since_last_cutoff}</p><p className="text-xs font-manrope text-dark/60">Orders since last cutoff</p></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-manrope font-bold text-dark/70 text-xs uppercase tracking-widest mb-2">Box Tier Mix</h3>
              {Object.keys(live.tier_mix).length === 0 ? <p className="text-sm text-dark/40 font-manrope">No orders yet</p> : Object.entries(live.tier_mix).map(([name, count]) => (
                <div key={name} className="flex justify-between text-sm font-manrope py-1" data-testid={`tier-mix-${name.replace(/\W/g, '-')}`}><span className="text-dark/70">{name}</span><strong>{count}</strong></div>
              ))}
            </div>
            <div>
              <h3 className="font-manrope font-bold text-dark/70 text-xs uppercase tracking-widest mb-2">Add-Ons Selected</h3>
              {Object.keys(live.addon_mix).length === 0 ? <p className="text-sm text-dark/40 font-manrope">No add-ons yet</p> : Object.entries(live.addon_mix).map(([name, count]) => (
                <div key={name} className="flex justify-between text-sm font-manrope py-1" data-testid={`addon-mix-${name.replace(/\W/g, '-')}`}><span className="text-dark/70">{name}</span><strong>{count}</strong></div>
              ))}
            </div>
            <div>
              <h3 className="font-manrope font-bold text-dark/70 text-xs uppercase tracking-widest mb-2">Route Counts</h3>
              {live.route_counts.map((r) => (
                <div key={r.name} className="flex justify-between text-sm font-manrope py-1" data-testid={`route-count-${r.name.replace(/\W/g, '-')}`}><span className="text-dark/70">{r.name} <span className="text-dark/40">({r.status})</span></span><strong>{r.count}</strong></div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
        <Stat icon={ClipboardCheck} label="Pending applications" value={d.pending_applications} to="/admin/applications" accent="bg-gold/15" />
        <Stat icon={PackageCheck} label="Products to review" value={d.pending_products} to="/admin/product-approvals" accent="bg-gold/15" />
        <Stat icon={Store} label="Approved farms" value={d.approved_farms} to="/admin/farms" />
        <Stat icon={Truck} label="Open orders" value={d.open_orders} to="/admin/orders" />
        <Stat icon={AlertTriangle} label="Shortages" value={d.shortages} to="/admin/orders" accent="bg-red-50" />
        <Stat icon={DollarSign} label="Pending payments" value={d.pending_payments} to="/admin/payments" accent="bg-gold/15" />
        <Stat icon={Store} label="Total farms" value={d.total_farms} to="/admin/farms" />
      </div>

      <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest/10">
        <h2 className="font-playfair text-2xl font-bold text-dark mb-6">Application Pipeline</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {Object.entries(pipeline).map(([status, count]) => (
            <Link to="/admin/applications" key={status} className="bg-cream rounded-xl p-4 text-center hover:bg-forest/5 transition-colors" data-testid={`pipeline-${status.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <p className="font-playfair text-2xl font-bold text-forest">{count}</p>
              <p className="font-manrope text-xs text-dark/60 mt-1">{status}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
